import classNames from "classnames";
import React, { MouseEvent, ReactElement, ReactNode, RefObject, useRef } from "react";
import Tooltip, { TooltipSize } from "../Tooltip/Tooltip";
import { handleMouseUpByBlurring } from "../../../utils/focus";
import * as utils from "../../../utils";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";

type OptionValue = string | boolean | number;

enum SegmentedControlSize {
  Medium = "medium",
  Large = "large",
}
enum SegmentedControlTheme {
  Light = "light",
  Dark = "dark",
}

interface ISegmentedControlOption<T extends OptionValue> {
  label: string | React.ReactNode;
  value: T;
  icon?: ReactElement;
  tooltipMessage?: string;
}

interface ISegmentedControlProps<T extends OptionValue> {
  size?: SegmentedControlSize;
  theme?: SegmentedControlTheme;
  label: string;
  tooltipSize?: TooltipSize;
  options: ISegmentedControlOption<T>[];
  selectedValue: T;
  onClickHandler: (optionValue: T) => void;
  labelHidden?: boolean;
}

// Stricter interface which only accept string values so that OptionGroup
// can be used in the Form component
interface IStricterOptionGroupOption extends ISegmentedControlOption<string> {
  value: string;
}

interface IStricterOptionGroupProps extends ISegmentedControlProps<string> {
  options: IStricterOptionGroupOption[];
  selectedValue: string;
  onClickHandler: (value: string) => void;
}

const renderOption = <T extends OptionValue>(
  selectedIndex: number,
  buttonRef: RefObject<HTMLButtonElement>,
  size: SegmentedControlSize,
  option: ISegmentedControlOption<T>,
  selectedValue: T,
  onClickHandler: (optionValue: T) => void,
  index: number,
  length: number,
  updateIndicatorPosition: () => void,
  tooltipSize?: TooltipSize,
): JSX.Element => {
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onClickHandler(option.value);
  };

  const isSelected = selectedValue === option.value;
  const isFirst = index === 0;

  const optionComponent = (
    <button
      role="radio"
      ref={buttonRef}
      aria-label={option.icon && typeof option.label === "string" ? option.label : undefined}
      aria-checked={isSelected}
      key={option.value.toString()}
      className={classNames("segmented-control__button", `segmented-control__button--${size}`, "center", {
        "segmented-control__button--selected": isSelected,
        "segmented-control__button--first": isFirst,
        "segmented-control__button--last": index === length - 1,
      })}
      onClick={handleClick}
      onMouseUp={handleMouseUpByBlurring}
    >
      <ReactResizeDetector handleWidth onResize={updateIndicatorPosition}>
        <>
          <div
            className={classNames("segmented-control__button-divider", {
              "segmented-control__button-divider--show": !isFirst && !isSelected && index !== selectedIndex + 1,
            })}
          ></div>
          <div className="segmented-control__button-content center">{renderOptionContent(option)}</div>
        </>
      </ReactResizeDetector>
    </button>
  );

  if (option.tooltipMessage !== undefined) {
    return (
      <Tooltip size={tooltipSize} key={option.value.toString()} content={option.tooltipMessage}>
        {optionComponent}
      </Tooltip>
    );
  } else {
    return optionComponent;
  }
};

const SegmentedControl = <T extends OptionValue>(props: ISegmentedControlProps<T>): JSX.Element => {
  const { options, selectedValue, onClickHandler, tooltipSize } = props;
  const size = props.size || SegmentedControlSize.Medium;
  const theme = props.theme || SegmentedControlTheme.Light;

  const [indicatorPosition, setIndicatorPosition] = React.useState<{ left?: number; width?: number }>();

  const optionButtonRefs = useRef<React.RefObject<HTMLButtonElement>[]>([]);
  const labelId = useRef(`segmented-control-${utils.randomString()}`).current;
  const selectedIndex = options.findIndex((option) => option.value === selectedValue);

  const updateIndicatorPosition = () => {
    const selectedButtonRef = optionButtonRefs.current[selectedIndex];
    const selectedButtonRect = selectedButtonRef?.current?.getBoundingClientRect();

    setIndicatorPosition({
      left: selectedButtonRef?.current?.offsetLeft,
      width: selectedButtonRect?.width,
    });
  };

  React.useLayoutEffect(() => {
    updateIndicatorPosition();
  }, [selectedValue]);

  const optionComponents = options.map((option, index) => {
    const buttonRef = React.createRef<HTMLButtonElement>();
    optionButtonRefs.current[index] = buttonRef;

    return renderOption(
      selectedIndex,
      buttonRef,
      size,
      option,
      selectedValue,
      onClickHandler,
      index,
      options.length,
      updateIndicatorPosition,
      tooltipSize,
    );
  });

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelId}
      className={classNames("segmented-control", `segmented-control--${theme}`)}
    >
      <label
        id={labelId}
        className={classNames("segmented-control__label", {
          "segmented-control__label--hidden": props.labelHidden,
        })}
      >
        {props.label}
      </label>
      <div className={classNames("segmented-control__content", `segmented-control__content--${size}`)}>
        {!!indicatorPosition?.width && (
          <div
            className="segmented-control__indicator"
            style={{
              transform: `translate3d(${indicatorPosition.left}px, 0, 0)`,
              width: indicatorPosition.width,
            }}
          />
        )}
        {optionComponents}
      </div>
    </div>
  );
};

// This is a stricter version of the OptionGroup which only accept string value
// to be used in the form for consistency
const StricterOptionGroup = (props: IStricterOptionGroupProps): JSX.Element => {
  return <SegmentedControl {...props} />;
};

const renderOptionContent = <T extends OptionValue>(option: ISegmentedControlOption<T>): ReactNode => {
  if (option.icon) {
    return option.icon;
  } else {
    return option.label;
  }
};

export default SegmentedControl;
export {
  ISegmentedControlOption,
  ISegmentedControlProps,
  IStricterOptionGroupProps,
  StricterOptionGroup,
  OptionValue,
  SegmentedControlSize,
  SegmentedControlTheme,
};
